import { useRef, useState } from 'react'
import { Button } from '@teach/ui'
import { useAppDispatch } from '@/store/hooks'
import { creditsApi, useBuyCreditsMutation } from '@/features/ai/creditsApi'
import {
  isCardHiddenForEmail,
  methodsForPackage,
  type CreditPackage,
  type PaymentMethod,
} from './lib/paymentMethods'
import { buyFailureText, parseBuyError, type BuyFailureView } from './lib/buyFailureText'
import { ContactEmailForm } from './ContactEmailForm'
import { MethodTiles } from './MethodTiles'
import type { CardPaymentAccess } from './useCardPaymentAccess'
import type { PendingPayment } from './CreditsDialogContext'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  pkg: CreditPackage
  /** Доступность карты и причина её отсутствия (`useCardPaymentAccess`). */
  cardAccess: CardPaymentAccess
  /** Страна профиля (PRD #71, п.28): `RU` — только карта, иначе только крипта. */
  country: string | null
  /**
   * Возврат к витрине. Необязательный аргумент — объяснение, которое переживёт
   * этот шаг: отказ `reload` (пакет исчез) размонтирует сам выбор способа,
   * и текст в локальном стейте пропал бы вместе с ним.
   */
  onBack: (notice?: string) => void
  /** Транзакция заведена: провайдер начинает ждать оплату. */
  onPaid: (payment: PendingPayment) => void
}

export const MethodPicker = ({ pkg, cardAccess, country, onBack, onPaid }: Props) => {
  const dispatch = useAppDispatch()
  const m = useMessages(messages)
  const canPayByCard = cardAccess === 'allowed'
  const methods = methodsForPackage(pkg, canPayByCard, country)
  const cardHidden = isCardHiddenForEmail(pkg, canPayByCard, country)
  // Почему рублёвого способа нет. Причина НАЗЫВАЕТСЯ только когда она известна:
  // непрочитанный профиль — это не «почты нет», и утверждать почту на сетевой
  // ошибке значило бы соврать автору, у которого она есть.
  const cause = cardAccess === 'no-email' ? m.noEmail : m.rubUnavailable
  // Почту предлагаем ввести ТОЛЬКО когда причина известна: на непрочитанном
  // профиле поле обещало бы, что после сохранения способ появится, а он мог
  // и не пропадать по этой причине вовсе.
  const askEmail = cardHidden && cardAccess === 'no-email'
  const [selected, setSelected] = useState<PaymentMethod | null>(null)
  const [failure, setFailure] = useState<BuyFailureView | null>(null)
  const [buy, { isLoading }] = useBuyCreditsMutation()
  const inFlight = useRef(false)
  // Выбор хранится как намерение: профиль мог доехать позже, и список способов
  // сменился (null -> RU), поэтому устаревший `selected` откатывается на первый.
  const method = methods.find((m) => m.id === selected) ?? methods[0] ?? null

  // Способов не осталось: либо обе цены не предложены, либо единственная была
  // рублёвой, а почты в профиле нет. Пустая группа выбора без слов была бы тем
  // же молчаливым «непонятно, что делать», что и пустая витрина.
  if (methods.length === 0)
    return (
      <div className="space-y-3">
        <p className="text-center text-sm text-muted-foreground">
          {cardHidden ? m.nothingToPayWith(cause) : m.packageNotPayable}
        </p>
        {/* Тупик лечится одним запросом — форма нужна тут даже сильнее, чем
            рядом со списком способов: других способов у пакета нет. */}
        {askEmail ? <ContactEmailForm /> : null}
        <div className="text-center">
          <Button variant="outline" size="sm" onClick={() => onBack()}>
            {m.toPackages}
          </Button>
        </div>
      </div>
    )

  const pay = async () => {
    // Флага загрузки мало: он доезжает до кнопки только следующим рендером, а
    // два клика подряд успевают уйти в одном. Цена лишнего клика — ВТОРАЯ
    // транзакция: воронка откроется дважды, и автор может заплатить дважды.
    if (!method || inFlight.current) return
    inFlight.current = true
    setFailure(null)
    try {
      const result = await buy({ packageId: pkg.id, currency: method.id }).unwrap()
      onPaid({
        transactionId: result.transactionId,
        paymentUrl: result.paymentUrl,
        method: method.id,
      })
    } catch (error) {
      const view = buyFailureText(parseBuyError(error))
      // `back` — остаёмся здесь, способ можно сменить.
      if (view.action !== 'reload') {
        setFailure(view)
        return
      }
      // `reload` — витрина устарела. Перечитывание принудительное: тега у
      // запроса пакетов нет намеренно (бэк кеширует их час), и без этой строки
      // исчезнувший пакет остался бы в сетке, а клик по нему давал бы тот же
      // отказ по кругу. Текст уводим наверх — покажет его уже шаг витрины.
      void dispatch(
        creditsApi.endpoints.getCreditPackages.initiate(undefined, {
          forceRefetch: true,
          subscribe: false,
        }),
      )
      onBack(view.title)
    } finally {
      inFlight.current = false
    }
  }

  return (
    <div className="space-y-4">
      <MethodTiles methods={methods} value={method?.id ?? null} onChange={setSelected} />
      {/* Пропавший способ объясняется, а не молчит: иначе автор, привыкший
          платить картой, читает её отсутствие как поломку витрины. */}
      {cardHidden ? <p className="text-sm text-muted-foreground">{cause}</p> : null}
      {askEmail ? <ContactEmailForm /> : null}
      {failure ? <p className="text-sm text-destructive">{failure.title}</p> : null}
      <div className="flex gap-2">
        <Button variant="ghost" onClick={() => onBack()}>
          {m.back}
        </Button>
        <Button className="flex-1" disabled={!method || isLoading} onClick={() => void pay()}>
          {method ? m.payPrice(method.price) : m.pay}
        </Button>
      </div>
    </div>
  )
}
